import {
  FormHelperText,
  Theme,
  styled,
  useTheme,
  FormHelperTextProps,
} from '@mui/material';
import React from 'react';
import { Icon, IconName } from './icon';

export type InputHelperTextProps = {
  theme: Theme;
  error?: boolean;
  success?: boolean;
  info?: boolean;
  warning?: boolean;
};

type HelperTextProps = FormHelperTextProps &
  Omit<InputHelperTextProps, 'theme'> & {
    text: string | string[] | React.ReactElement | undefined;
  };

const AttentionIcon: React.FC<{ color?: string; iconName?: IconName }> = ({
  color,
  iconName,
}) => {
  const theme = useTheme();

  return (
    <Icon
      sx={{ pr: '5px' }}
      iconName={iconName ?? 'check-circle-full'}
      color={color ?? theme.palette.success.main}
      size="medium"
    />
  );
};

const ErrorIcon = () => {
  const theme = useTheme();
  return (
    <Icon
      sx={{ pr: '5px' }}
      iconName="warning"
      color={theme.palette.error.main}
      size="medium"
    />
  );
};

export const StyledHelperText = styled(FormHelperText)(
  ({ theme, error, success, info, warning }: InputHelperTextProps) => {
    const baseStyle = {
      fontFamily: 'HCo Gotham SSm',
      fontWeight: 400,
      fontSize: '12px',
      // color: theme.palette.greyscale['04'],
      color: theme.palette.greyscale.white,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    };
    if (error) return { ...baseStyle, color: theme.palette.error.main };
    if (success) return { ...baseStyle, color: theme.palette.success.main };
    if (warning) return { ...baseStyle, color: theme.palette.warning.main };
    if (info) return { ...baseStyle, color: theme.palette.greyscale['04'] };

    return baseStyle;
  },
);

export const InputHelperText: React.FC<HelperTextProps> = ({
  error,
  success,
  info,
  warning,
  text,
}) => {
  if (!text) return null;

  let icon = null;

  if (error) icon = <ErrorIcon />;
  else if (success) icon = <AttentionIcon color="success" />;
  else if (warning) icon = <AttentionIcon color="warning" iconName="error" />;
  else if (info) icon = <AttentionIcon color="info" iconName="error" />;

  return (
    <StyledHelperText
      component="label"
      error={error ? true : undefined}
      success={success ? 'true' : undefined}
      info={info ? 'true' : undefined}
      warning={warning ? 'true' : undefined}
    >
      {icon && icon}
      {text}
    </StyledHelperText>
  );
};

export default StyledHelperText;
